import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Users, Edit2, Trash2, Power, PowerOff, Filter } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import turmasService, { TurnoEnum } from '../services/turmasService';
import type { TurmaResponse } from '../services/turmasService';
import TurmaFormModal from '../components/TurmaFormModal';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import { Container, PageHeader, PageTitle, EmptyState as ThemeEmptyState } from '../styles/components/common';
import { BaseButton } from '../styles/components/buttons';
import { Select as ThemeSelect } from '../styles/components/forms';
import {
  FiltersContainer,
  FilterLabel,
  GridContainer,
  TurmaCard,
  TurmaHeader,
  TurmaInfo,
  TurmaTitle,
  TurmaSubtitle,
  StatusBadge,
  TurmaDetails,
  DetailItem,
  DetailLabel,
  DetailValue,
  StudentsInfo,
  StudentsCount,
  ActionButtons,
  IconButton,
  EmptyIcon
} from './TurmasPage.styles';

const TurmasPage: React.FC = () => {
  const { user } = useAuth();
  const [turmas, setTurmas] = useState<TurmaResponse[]>([]);
  const [filteredTurmas, setFilteredTurmas] = useState<TurmaResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTurma, setEditingTurma] = useState<TurmaResponse | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; turma: TurmaResponse | null }>({
    show: false,
    turma: null
  });
  
  // Filtros
  const [filterAno, setFilterAno] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');

  const isAdmin = user?.tipo === 'admin';

  const loadTurmas = async () => {
    try {
      setLoading(true);
      const data = await turmasService.findAll();
      setTurmas(data);
      setFilteredTurmas(data);
    } catch (error) {
      console.error('Erro ao carregar turmas:', error);
      toast.error('Erro ao carregar turmas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTurmas();
  }, []);

  // Aplicar filtros
  useEffect(() => {
    let filtered = [...turmas];

    if (filterAno) {
      filtered = filtered.filter(turma => turma.ano.toString() === filterAno);
    }

    if (filterStatus === 'ativa') {
      filtered = filtered.filter(turma => turma.ativa);
    } else if (filterStatus === 'inativa') {
      filtered = filtered.filter(turma => !turma.ativa);
    }

    setFilteredTurmas(filtered);
  }, [turmas, filterAno, filterStatus]);

  const handleCreateTurma = () => {
    setEditingTurma(null);
    setShowModal(true);
  };

  const handleEditTurma = (turma: TurmaResponse) => {
    setEditingTurma(turma);
    setShowModal(true);
  };

  const handleDeleteTurma = (turma: TurmaResponse) => {
    setDeleteModal({ show: true, turma });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.turma) return;

    try {
      await turmasService.remove(deleteModal.turma.id);
      toast.success('Turma excluída com sucesso');
      await loadTurmas();
    } catch (error: any) {
      console.error('Erro ao excluir turma:', error);
      if (error.response?.status === 409) {
        toast.error('Não é possível excluir turma que possui alunos cadastrados');
      } else {
        toast.error('Erro ao excluir turma');
      }
    } finally {
      setDeleteModal({ show: false, turma: null });
    }
  };

  const handleToggleActive = async (turma: TurmaResponse) => {
    try {
      await turmasService.toggleActive(turma.id);
      toast.success(`Turma ${turma.ativa ? 'desativada' : 'ativada'} com sucesso`);
      await loadTurmas();
    } catch (error) {
      console.error('Erro ao alterar status da turma:', error);
      toast.error('Erro ao alterar status da turma');
    }
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    loadTurmas();
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getTurnoLabel = (turno: string) => {
    const labels: Record<string, string> = {
      [TurnoEnum.MATUTINO]: 'Matutino',
      [TurnoEnum.VESPERTINO]: 'Vespertino',
      [TurnoEnum.NOTURNO]: 'Noturno',
      [TurnoEnum.INTEGRAL]: 'Integral'
    };
    return labels[turno] || turno;
  };

  // Obter anos únicos para o filtro
  const anosDisponiveis = [...new Set(turmas.map(t => t.ano))].sort((a, b) => b - a);

  if (loading) {
    return (
      <Layout title="Turmas">
        <Container>
          <div style={{ textAlign: 'center', padding: '60px' }}>
            Carregando turmas...
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="Turmas">
      <Container>
        <PageHeader>
          <PageTitle>Gestão de Turmas</PageTitle>
          {isAdmin && (
            <BaseButton onClick={handleCreateTurma}>
              <Plus size={20} />
              Nova Turma
            </BaseButton>
          )}
        </PageHeader>

        <FiltersContainer>
          <Filter size={20} color="#666" />
          <FilterLabel>
            Ano
            <ThemeSelect value={filterAno} onChange={(e) => setFilterAno(e.target.value)}>
              <option value="">Todos os anos</option>
              {anosDisponiveis.map(ano => (
                <option key={ano} value={ano.toString()}>{ano}</option>
              ))}
            </ThemeSelect>
          </FilterLabel>
          <FilterLabel>
            Status
            <ThemeSelect value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">Todos</option>
              <option value="ativa">Ativas</option>
              <option value="inativa">Inativas</option>
            </ThemeSelect>
          </FilterLabel>
        </FiltersContainer>

        {filteredTurmas.length === 0 ? (
          <ThemeEmptyState>
            <EmptyIcon>🏫</EmptyIcon>
            <h3>Nenhuma turma encontrada</h3>
            <p>
              {turmas.length === 0 
                ? 'Comece criando sua primeira turma.' 
                : 'Ajuste os filtros para encontrar turmas.'}
            </p>
            {isAdmin && turmas.length === 0 && (
              <BaseButton onClick={handleCreateTurma} style={{ margin: '20px auto' }}>
                <Plus size={20} />
                Criar Primeira Turma
              </BaseButton>
            )}
          </ThemeEmptyState>
        ) : (
          <GridContainer>
            {filteredTurmas.map((turma) => (
              <TurmaCard key={turma.id} ativa={turma.ativa}>
                <TurmaHeader>
                  <TurmaInfo>
                    <TurmaTitle>{turma.nome}</TurmaTitle>
                    <TurmaSubtitle>{turma.serie} - {getTurnoLabel(turma.turno)}</TurmaSubtitle>
                  </TurmaInfo>
                  <StatusBadge ativa={turma.ativa}>
                    {turma.ativa ? 'Ativa' : 'Inativa'}
                  </StatusBadge>
                </TurmaHeader>

                <TurmaDetails>
                  <DetailItem>
                    <DetailLabel>Ano</DetailLabel>
                    <DetailValue>{turma.ano}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Mensalidade</DetailLabel>
                    <DetailValue>{formatCurrency(turma.valorMensalidade)}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>NFSe</DetailLabel>
                    <DetailValue>
                      {turma.codigoServico
                        ? `${turma.codigoServico.codigoAtividade} (${turma.codigoServico.codigoServico})`
                        : 'Nao definido'}
                    </DetailValue>
                  </DetailItem>
                </TurmaDetails>

                <StudentsInfo>
                  <Users size={16} color="#007bff" />
                  <StudentsCount>{turma.totalAlunos || 0}</StudentsCount>
                  <span>aluno{(turma.totalAlunos || 0) !== 1 ? 's' : ''}</span>
                </StudentsInfo>

                {isAdmin && (
                  <ActionButtons>
                    <IconButton 
                      variant="edit" 
                      onClick={() => handleEditTurma(turma)}
                      title="Editar turma"
                    >
                      <Edit2 size={16} />
                    </IconButton>
                    <IconButton 
                      variant="toggle"
                      onClick={() => handleToggleActive(turma)}
                      title={turma.ativa ? 'Desativar turma' : 'Ativar turma'}
                    >
                      {turma.ativa ? <PowerOff size={16} /> : <Power size={16} />}
                    </IconButton>
                    <IconButton 
                      variant="delete" 
                      onClick={() => handleDeleteTurma(turma)}
                      title="Excluir turma"
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </ActionButtons>
                )}
              </TurmaCard>
            ))}
          </GridContainer>
        )}

        {showModal && (
          <TurmaFormModal
            turma={editingTurma}
            onSuccess={handleModalSuccess}
            onClose={() => setShowModal(false)}
          />
        )}

        {deleteModal.show && (
          <ConfirmDeleteModal
            title="Excluir Turma"
            message={`Tem certeza que deseja excluir a turma "${deleteModal.turma?.nome}"?`}
            onConfirm={handleConfirmDelete}
            onCancel={() => setDeleteModal({ show: false, turma: null })}
          />
        )}
      </Container>
    </Layout>
  );
};

export default TurmasPage;
